import { Dispatch } from "redux";
import { REQUEST_REFERENCE_LABELS } from "./ReferenceLabel.reducer";

/**
 * Batching buffer for read-only reference-label resolution.
 *
 * Every read-only reference cell that needs an id resolved calls
 * `enqueueReferenceLabelRequest`. Instead of dispatching (and fetching) once per
 * cell, requests are accumulated per resource and flushed on a microtask, so a
 * screenful of cells that mount in the same tick produces ONE
 * REQUEST_REFERENCE_LABELS dispatch per resource (the saga then issues one
 * `{id in [...]}` query). Ids are deduplicated within a flush via a Set; ids
 * already `loading`/`resolved` in the store are guarded out by the caller
 * (useReferenceLabel) before enqueueing.
 */
let pending: Map<string, Set<string>> = new Map();
let scheduled = false;
let pendingDispatch: Dispatch<any> | null = null;

const flush = () => {
    scheduled = false;
    const batches = pending;
    pending = new Map();
    const dispatch = pendingDispatch;
    pendingDispatch = null;
    if (!dispatch) {
        return;
    }
    batches.forEach((ids, resource) => {
        dispatch({
            type: REQUEST_REFERENCE_LABELS,
            resource,
            ids: Array.from(ids),
        });
    });
};

export const enqueueReferenceLabelRequest = (
    resource: string,
    id: string,
    dispatch: Dispatch<any>
) => {
    pendingDispatch = dispatch;
    let ids = pending.get(resource);
    if (!ids) {
        ids = new Set<string>();
        pending.set(resource, ids);
    }
    ids.add(id);
    if (!scheduled) {
        scheduled = true;
        Promise.resolve().then(flush);
    }
};

/** Test-only: clear the module-level buffer between test cases. */
export const __resetReferenceLabelBuffer = () => {
    pending = new Map();
    scheduled = false;
    pendingDispatch = null;
};
